// src/pages/Users/Techniques/components/TechniquesToolbar.jsx

import React, { useMemo, useState } from "react";
import {
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaHeart,
  FaRedo,
  FaSearch,
  FaShareAlt,
  FaSlidersH,
} from "react-icons/fa";

import {
  DEFAULT_TECHNIQUES_STATE,
  hasActiveFilters,
} from "../utils/techniquesUtils";

export default function TechniquesToolbar({
  dynamicOptions,
  ui,
  onChange,
  searchInput,
  setSearchInput,
  onReset,
  onShare,
  shareCopied,
  filteredCount,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFilters = useMemo(() => hasActiveFilters(ui), [ui]);

  return (
    <div className="rounded-[1.75rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
      {/* Barre principale */}
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400" />

          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Rechercher une technique..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:bg-slate-950"
          />
        </div>

        <ToolbarIconButton
          active={ui.showFav}
          onClick={() => onChange({ showFav: !ui.showFav })}
          title={
            ui.showFav
              ? "Afficher toutes les techniques"
              : "Afficher mes favoris"
          }
        >
          <FaHeart />
        </ToolbarIconButton>

        <ToolbarIconButton
          active={shareCopied}
          onClick={onShare}
          title={shareCopied ? "Lien copié" : "Copier le lien"}
        >
          {shareCopied ? <FaCheck /> : <FaShareAlt />}
        </ToolbarIconButton>

        <button
          type="button"
          onClick={() => setFiltersOpen((prev) => !prev)}
          className={`inline-flex h-12 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-bold shadow-sm transition active:scale-95 md:hidden ${
            filtersOpen
              ? "bg-sky-50 text-sky-700 ring-1 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:ring-sky-900"
              : "bg-slate-950 text-white dark:bg-white dark:text-slate-950"
          }`}
        >
          <FaSlidersH />
          <span>Filtres</span>
          {filtersOpen ? (
            <FaChevronUp className="text-xs" />
          ) : (
            <FaChevronDown className="text-xs" />
          )}
        </button>
      </div>

      {/* Infos mobiles */}
      <div className="mt-3 flex items-center justify-between gap-3 md:hidden">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
          {filteredCount} résultat{filteredCount > 1 ? "s" : ""}
        </p>

        {activeFilters && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaRedo className="text-[11px]" />
            Réinitialiser
          </button>
        )}
      </div>

      {/* Filtres desktop */}
      <div className="mt-4 hidden grid-cols-3 gap-3 md:grid">
        <FiltersContent
          dynamicOptions={dynamicOptions}
          ui={ui}
          onChange={onChange}
        />
      </div>

      {/* 
        ✅ Filtres mobile
        Le panneau se déroule SOUS la toolbar, 
        dans le flux normal, sans fixed et sans backdrop.
      */}
      {filtersOpen && (
        <div className="mt-3 md:hidden">
          <div className="max-h-[calc(100dvh-180px)] overflow-y-auto rounded-[1.5rem] border border-slate-200 bg-slate-50 p-3 shadow-inner dark:border-slate-800 dark:bg-slate-950">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                  Affichage
                </p>
                <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                  Affiner les techniques
                </h3>
              </div>

              {activeFilters && (
                <button
                  type="button"
                  onClick={onReset}
                  className="rounded-full bg-white px-3 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition active:scale-95 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                >
                  Reset
                </button>
              )}
            </div>

            <div className="space-y-3">
              <FiltersContent
                dynamicOptions={dynamicOptions}
                ui={ui}
                onChange={onChange}
                stacked
              />
            </div>

            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              className="mt-4 flex min-h-12 w-full items-center justify-center rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
            >
              Voir les résultats
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FiltersContent({ dynamicOptions, ui, onChange, stacked = false }) {
  const wrapperClass = stacked ? "space-y-3" : "contents";

  return (
    <div className={wrapperClass}>
      <FilterSelect
        label="Ceinture"
        value={ui.grade}
        options={dynamicOptions.grades}
        onChange={(value) => onChange({ grade: value })}
      />

      <FilterSelect
        label="Famille"
        value={ui.family}
        options={dynamicOptions.families}
        onChange={(value) => onChange({ family: value })}
      />

      <FilterSelect
        label="Tri"
        value={ui.sort}
        options={[
          { value: "beltFamily", label: "Ceinture ▸ Famille ▸ Nom" },
          { value: "alpha", label: "Tri alphabétique" },
          { value: "grade", label: "Tri par ceinture" },
          { value: "family", label: "Tri par famille" },
        ]}
        onChange={(value) => onChange({ sort: value })}
      />
    </div>
  );
}

function FilterSelect({ label, value, options, onChange, getLabel }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-800 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
      >
        {options.map((option) => {
          const optionValue =
            typeof option === "string" ? option : option.value;

          const optionLabel =
            typeof option === "string"
              ? getLabel
                ? getLabel(option)
                : option
              : option.label;

          return (
            <option key={optionValue} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </select>
    </label>
  );
}

function ToolbarIconButton({ children, active, onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border text-base transition active:scale-95 ${
        active
          ? "border-red-200 bg-red-50 text-red-500 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300"
          : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
      }`}
    >
      {children}
    </button>
  );
}
